/**
 * MpShell — a shared visual wrapper for the 5 multiplayer PWAs.
 * Renders:
 *  - a wager picker + "find opponent" button when idle
 *  - a connecting / matchmaking pulse when waiting
 *  - a chip header (You vs Opponent, stake, status)
 *  - the game body (children) once paired
 *  - a settlement overlay (win/lose/tie, coin delta, AdReward, rematch)
 */
import { useState } from "react";
import { Coins, Zap, Users, Loader2, RotateCcw, Link as LinkIcon, Check, Trophy } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { createMpChallenge } from "@/lib/api";
import { toast } from "sonner";
import AdReward from "./_adReward";

const STAKES = [0, 5, 10, 25, 50];

export function MpShell({
  title, accent = "#00F0FF", game, gameSlug,
  status, self, opponent, settlement, stake, setStake,
  onFindOpponent, onRematch, canRematch = true, initialChallenge = null,
  children,
}) {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);

  const shareChallenge = async () => {
    try {
      const res = await createMpChallenge(game);
      const url = `${window.location.origin}${res.join_url}`;
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Challenge link copied!", { description: "Send it to a friend" });
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't create challenge link");
    }
  };

  return (
    <div className="min-h-screen bg-[#050510] text-white p-4 md:p-8 relative overflow-hidden">
      <div className="w-full max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <span className="w-10 h-10 rounded-xl flex items-center justify-center shadow-[0_0_20px_rgba(0,240,255,0.5)]" style={{ background: accent }}>
            <Users className="w-5 h-5 text-black" strokeWidth={3} />
          </span>
          <div>
            <div className="mono text-[9px] uppercase tracking-[0.25em]" style={{ color: accent }}>// jixxeo apps · multiplayer</div>
            <h1 className="font-heading font-black text-xl uppercase tracking-tighter leading-none">{title}</h1>
          </div>
        </div>

        {/* Chips */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          <MpChip testId="mp-chip-you" color={accent} label="You" value={self?.name || (user?.name || "You")} />
          <MpChip testId="mp-chip-opponent" color={accent} label="Opponent" value={opponent?.name || (status === "waiting" ? "…" : "—")} />
          <MpChip testId="mp-chip-stake" color="#FFE300" label="Stake" value={`${stake} coins`} icon={<Coins className="w-3 h-3" />} />
          <MpChip testId="mp-chip-status" color={status === "paired" ? "#39FF14" : "#FF003C"} label="Status" value={statusText(status)} />
        </div>

        {/* Idle state — pick wager + queue */}
        {status === "idle" && !initialChallenge && (
          <div className="glass rounded-2xl p-5 border border-white/10 space-y-4">
            <div className="mono text-[10px] uppercase tracking-widest text-white/60">Pick your wager</div>
            <div className="grid grid-cols-5 gap-2">
              {STAKES.map((s) => (
                <button key={s} data-testid={`mp-stake-${s}`}
                  onClick={() => setStake(s)}
                  className={`py-2 rounded-lg mono text-[10px] uppercase tracking-widest transition-colors ${stake === s ? "text-black font-bold" : "text-white/60 hover:text-white"}`}
                  style={{
                    background: stake === s ? accent : "rgba(255,255,255,0.04)",
                    border: `1px solid ${stake === s ? accent : "rgba(255,255,255,0.1)"}`,
                  }}>
                  {s === 0 ? "Free" : `${s}c`}
                </button>
              ))}
            </div>
            {stake > 0 && (user?.coins ?? 0) < stake && (
              <div className="mono text-[10px] text-[#FF003C]">Not enough coins. You have {user?.coins ?? 0}.</div>
            )}
            <button data-testid="mp-find" onClick={onFindOpponent}
              disabled={stake > 0 && (user?.coins ?? 0) < stake}
              className="w-full py-3 rounded-full font-black mono text-xs uppercase tracking-widest text-black transition-transform hover:scale-[1.02] disabled:opacity-40"
              style={{ background: accent }}>
              <span className="inline-flex items-center gap-2 justify-center">
                <Zap className="w-4 h-4" /> Find opponent
              </span>
            </button>
            <button data-testid="mp-share" onClick={shareChallenge}
              className="w-full py-2 rounded-full mono text-[10px] uppercase tracking-widest border border-white/15 text-white/70 hover:text-white hover:bg-white/5">
              <span className="inline-flex items-center gap-2 justify-center">
                {copied ? <Check className="w-3 h-3" /> : <LinkIcon className="w-3 h-3" />}
                {copied ? "Link copied" : "Or challenge a friend →"}
              </span>
            </button>
          </div>
        )}

        {/* Waiting for opponent */}
        {status === "waiting" && (
          <div className="glass rounded-2xl p-8 border border-white/10 text-center">
            <Loader2 className="w-10 h-10 mx-auto animate-spin" style={{ color: accent }} />
            <div className="mono text-[10px] uppercase tracking-widest text-white/60 mt-4">Matchmaking…</div>
            <div className="font-heading font-black text-2xl uppercase tracking-tighter mt-1">Finding an opponent</div>
            <div className="mono text-[10px] uppercase tracking-widest text-white/40 mt-3">
              If no one joins in 15 seconds, we'll pair you with a bot.
            </div>
          </div>
        )}

        {status === "connecting" && (
          <div className="glass rounded-2xl p-8 border border-white/10 text-center">
            <Loader2 className="w-8 h-8 mx-auto animate-spin" />
            <div className="mono text-xs uppercase tracking-widest mt-3">Connecting…</div>
          </div>
        )}

        {status === "error" && (
          <div className="glass rounded-2xl p-6 border border-[#FF003C]/50 text-center">
            <div className="font-heading font-black text-2xl text-[#FF003C] uppercase tracking-tighter">Connection failed</div>
            <div className="mono text-[10px] uppercase tracking-widest text-white/60 mt-2">Please try again.</div>
          </div>
        )}

        {/* Paired — render children (game body) */}
        {(status === "paired" || (status === "ended" && !settlement)) && (
          <div className="glass rounded-2xl p-5 border border-white/10">
            {children}
          </div>
        )}

        {/* Settled */}
        {status === "ended" && settlement && (
          <div className="glass rounded-2xl p-6 border border-[#FFE300]/40 text-center space-y-3">
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#FFE300]">// match complete</div>
            <SettleTitle settlement={settlement} />
            {settlement.you_gained !== 0 && (
              <div className="mono text-sm">
                {settlement.you_gained > 0
                  ? <span className="text-[#39FF14] font-bold">+{settlement.you_gained} coins</span>
                  : <span className="text-[#FF003C] font-bold">{settlement.you_gained} coins</span>}
                <span className="text-white/50"> · balance {settlement.coins}</span>
              </div>
            )}
            <div className="flex justify-center pt-2">
              <AdReward game={gameSlug || `jixxeo-${game}`} compact />
            </div>
            {canRematch && (
              <button onClick={onRematch} data-testid="mp-rematch"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-black mono text-xs uppercase tracking-widest px-6 py-3 rounded-full">
                <RotateCcw className="w-4 h-4" /> Rematch
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function statusText(s) {
  return { idle: "Ready", connecting: "…", waiting: "Matchmaking", paired: "Live", ended: "Done", error: "Error" }[s] || s;
}

function MpChip({ color, label, value, icon, testId }) {
  return (
    <span data-testid={testId}
      className="mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border flex items-center gap-1"
      style={{ borderColor: `${color}55`, background: `${color}10`, color }}>
      {icon}
      {label}: <span className="font-bold text-white ml-1 max-w-[80px] truncate">{value}</span>
    </span>
  );
}

function SettleTitle({ settlement }) {
  const g = settlement.you_gained;
  if (g > 0) return <div className="font-heading font-black text-3xl uppercase tracking-tighter text-[#39FF14]">Victory <Trophy className="inline w-6 h-6" /></div>;
  if (g < 0) return <div className="font-heading font-black text-3xl uppercase tracking-tighter text-[#FF003C]">Defeat</div>;
  return <div className="font-heading font-black text-3xl uppercase tracking-tighter text-[#FFE300]">Match over</div>;
}
